import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ActaModal from "@/components/ui/ActaModal";

function contactLabel(contact) {
  const fullName = `${
    contact?.firstName || ""
  } ${
    contact?.lastName || ""
  }`.trim();

  if (
    contact?.company &&
    fullName
  ) {
    return `${contact.company} — ${fullName}`;
  }

  return (
    contact?.company ||
    fullName ||
    "Contact"
  );
}

function defaultFollowupStart() {
  const start = new Date(
    Date.now() + 60 * 60 * 1000
  );

  start.setMinutes(
    Math.ceil(
      start.getMinutes() / 15
    ) * 15,
    0,
    0
  );

  return start;
}

function dateInputValue(date) {
  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");
  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export default function CommerceAgendaNewActionDialog({
  open,
  contacts = [],
  loading = false,
  saving = false,
  onMeeting,
  onCreateFollowup,
  onClose,
  initialMode = "choose",
  initialProspectId = "",
}) {
  const [mode, setMode] =
    useState("choose");

  const defaultContactId = useMemo(
    () => contacts?.[0]?.id || "",
    [contacts]
  );

  const [prospectId, setProspectId] =
    useState("");
  const [date, setDate] =
    useState("");
  const [time, setTime] =
    useState("");
  const [content, setContent] =
    useState("");
  const [channel, setChannel] =
    useState("other");

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    if (wasOpenRef.current) {
      return;
    }

    wasOpenRef.current = true;

    const start =
      defaultFollowupStart();

    setMode(
      initialMode === "followup"
        ? "followup"
        : "choose"
    );

    setProspectId(
      initialProspectId ||
      defaultContactId
    );

    setDate(
      dateInputValue(start)
    );

    setTime(
      start
        .toTimeString()
        .slice(0, 5)
    );

    setContent(
      initialMode === "followup"
        ? "Rappeler le contact"
        : ""
    );

    setChannel("other");
  }, [
    open,
    initialMode,
    initialProspectId,
    defaultContactId,
  ]);

  const ensureDefaults = () => {
    const start =
      defaultFollowupStart();

    if (!prospectId) {
      setProspectId(
        initialProspectId ||
        defaultContactId
      );
    }

    if (!date) {
      setDate(
        dateInputValue(start)
      );
    }

    if (!time) {
      setTime(
        start
          .toTimeString()
          .slice(0, 5)
      );
    }
  };

  const chooseFollowup = () => {
    ensureDefaults();
    setMode("followup");
  };

  const close = () => {
    setMode("choose");
    setProspectId("");
    setDate("");
    setTime("");
    setContent("");
    setChannel("other");
    onClose?.();
  };

  const submitFollowup = async (
    event
  ) => {
    event.preventDefault();

    const selectedProspectId =
      prospectId ||
      initialProspectId ||
      defaultContactId;

    if (!selectedProspectId) {
      return;
    }

    const ok =
      await onCreateFollowup?.({
        prospectId:
          selectedProspectId,
        date,
        time,
        content,
        channel,
      });

    if (ok) {
      close();
    }
  };

  return (
    <ActaModal
      open={open}
      onClose={close}
      eyebrow="Agenda commercial"
      title="Nouvelle action"
      subtitle={
        mode === "followup"
          ? "Planifier une relance"
          : "Choisissez le type d’action à créer"
      }
      size="md"
      closeDisabled={saving}
      testId="agenda-new-action-dialog"
    >
      {mode === "choose" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              const selectedProspectId =
                initialProspectId ||
                prospectId ||
                defaultContactId;

              close();
              onMeeting?.(
                selectedProspectId
              );
            }}
            className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-left transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
            data-testid="agenda-new-action-meeting"
          >
            <strong className="block font-heading text-base font-black text-[var(--do-ink)]">
              Rendez-vous
            </strong>

            <span className="mt-1 block text-sm leading-5 text-[var(--do-muted)]">
              Planifier un rendez-vous avec un prospect ou client.
            </span>
          </button>

          <button
            type="button"
            disabled={
              loading ||
              !contacts.length
            }
            onClick={chooseFollowup}
            className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-left transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
            data-testid="agenda-new-action-followup"
          >
            <strong className="block font-heading text-base font-black text-[var(--do-ink)]">
              Relance
            </strong>

            <span className="mt-1 block text-sm leading-5 text-[var(--do-muted)]">
              Créer une relance datée avec son contenu.
            </span>
          </button>
        </div>
      ) : (
        <form
          onSubmit={submitFollowup}
          className="grid gap-4 sm:grid-cols-2"
        >
          <label className="sm:col-span-2">
            <span className="text-xs font-bold text-[var(--do-muted)]">
              Prospect ou client
            </span>

            <select
              required
              value={
                prospectId ||
                initialProspectId ||
                defaultContactId
              }
              onChange={(event) =>
                setProspectId(
                  event.target.value
                )
              }
              className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)] outline-none transition focus:border-[var(--do-accent)] focus:ring-2 focus:ring-[var(--do-accent-soft)]"
              data-testid="agenda-new-followup-prospect"
            >
              {contacts.map(
                (contact) => (
                  <option
                    key={contact.id}
                    value={contact.id}
                  >
                    {contactLabel(
                      contact
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="sm:col-span-2">
            <span className="text-xs font-bold text-[var(--do-muted)]">
              Canal de relance
            </span>

            <select
              value={channel}
              onChange={(event) =>
                setChannel(
                  event.target.value
                )
              }
              className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)] outline-none transition focus:border-[var(--do-accent)] focus:ring-2 focus:ring-[var(--do-accent-soft)]"
              data-testid="agenda-new-followup-channel"
            >
              <option value="email">
                Email
              </option>

              <option value="phone">
                Téléphone
              </option>

              <option value="whatsapp">
                WhatsApp
              </option>

              <option value="other">
                Autre / non précisé
              </option>
            </select>
          </label>

          <label>
            <span className="text-xs font-bold text-[var(--do-muted)]">
              Date
            </span>

            <input
              required
              type="date"
              value={date}
              onChange={(event) =>
                setDate(
                  event.target.value
                )
              }
              className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)] outline-none transition focus:border-[var(--do-accent)] focus:ring-2 focus:ring-[var(--do-accent-soft)]"
            />
          </label>

          <label>
            <span className="text-xs font-bold text-[var(--do-muted)]">
              Heure
            </span>

            <input
              required
              type="time"
              value={time}
              onChange={(event) =>
                setTime(
                  event.target.value
                )
              }
              className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)] outline-none transition focus:border-[var(--do-accent)] focus:ring-2 focus:ring-[var(--do-accent-soft)]"
            />
          </label>

          <label className="sm:col-span-2">
            <span className="text-xs font-bold text-[var(--do-muted)]">
              Relance
            </span>

            <textarea
              required
              rows={4}
              maxLength={500}
              value={content}
              onChange={(event) =>
                setContent(
                  event.target.value
                )
              }
              className="mt-2 w-full resize-y rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-base text-[var(--do-ink)] outline-none transition focus:border-[var(--do-accent)] focus:ring-2 focus:ring-[var(--do-accent-soft)]"
              placeholder="Décrire la relance à effectuer"
              data-testid="agenda-new-followup-content"
            />
          </label>

          <div className="sm:col-span-2 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                setMode("choose")
              }
              className="min-h-[48px] rounded-xl border border-[var(--do-border)] px-5 text-sm font-black text-[var(--do-ink)] hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
            >
              Retour
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                loading ||
                !contacts.length
              }
              className="min-h-[48px] rounded-xl bg-[var(--do-accent)] px-5 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="agenda-new-followup-save"
            >
              {saving
                ? "Création…"
                : "Créer la relance"}
            </button>
          </div>
        </form>
      )}
    </ActaModal>
  );
}
